import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.35;

export const useInlineImageZoom = (imageUrl?: string) => {
    const [zoom, setZoom] = useState(MIN_ZOOM);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
    const didDragRef = useRef(false);
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;

    const resetView = useCallback(() => {
        setZoom(MIN_ZOOM);
        setPan({ x: 0, y: 0 });
        setIsDragging(false);
        didDragRef.current = false;
    }, []);

    useEffect(() => {
        resetView();
    }, [imageUrl, resetView]);

    const clampZoom = useCallback(
        (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value.toFixed(2)))),
        [],
    );

    const applyZoom = useCallback(
        (nextZoom: number) => {
            const clamped = clampZoom(nextZoom);
            setZoom(clamped);
            if (clamped <= MIN_ZOOM) {
                setPan({ x: 0, y: 0 });
            }
        },
        [clampZoom],
    );

    useEffect(() => {
        const el = viewportRef.current;
        if (!el || !imageUrl) {
            return;
        }

        const onWheel = (event: WheelEvent) => {
            event.preventDefault();
            event.stopPropagation();
            const direction = event.deltaY < 0 ? 1 : -1;
            const next = Math.min(
                MAX_ZOOM,
                Math.max(MIN_ZOOM, Number((zoomRef.current + direction * ZOOM_STEP).toFixed(2))),
            );
            setZoom(next);
            if (next <= MIN_ZOOM) {
                setPan({ x: 0, y: 0 });
            }
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [imageUrl]);

    const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!imageUrl || zoom <= MIN_ZOOM) {
            didDragRef.current = false;
            return;
        }
        setIsDragging(true);
        didDragRef.current = false;
        dragStartRef.current = {
            x: event.clientX,
            y: event.clientY,
            panX: pan.x,
            panY: pan.y,
        };
        event.preventDefault();
    };

    const handleMouseMove = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (!isDragging) {
            return;
        }
        const dx = event.clientX - dragStartRef.current.x;
        const dy = event.clientY - dragStartRef.current.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
            didDragRef.current = true;
        }
        setPan({
            x: dragStartRef.current.panX + dx,
            y: dragStartRef.current.panY + dy,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleDoubleClick = () => {
        if (didDragRef.current) {
            return;
        }
        if (zoom > MIN_ZOOM) {
            resetView();
            return;
        }
        applyZoom(3);
    };

    let viewportCursor: 'default' | 'grab' | 'grabbing' | 'zoom-in' = imageUrl ? 'zoom-in' : 'default';
    if (zoom > MIN_ZOOM) {
        viewportCursor = isDragging ? 'grabbing' : 'grab';
    }

    return {
        zoom,
        pan,
        isDragging,
        viewportRef,
        viewportCursor,
        minZoom: MIN_ZOOM,
        maxZoom: MAX_ZOOM,
        zoomStep: ZOOM_STEP,
        resetView,
        applyZoom,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleDoubleClick,
        canZoomIn: Boolean(imageUrl) && zoom < MAX_ZOOM,
        canZoomOut: Boolean(imageUrl) && zoom > MIN_ZOOM,
        canReset: Boolean(imageUrl) && (zoom > MIN_ZOOM || pan.x !== 0 || pan.y !== 0),
    };
};
